// "Device data" (D111, APS-358): ask one install or one app user to send what its device holds
// to Apsio, or to delete it there. The request rides in the policy every device of the project
// fetches, matched by hash; the API keeps only its last four characters. Owners and admins only:
// members see the button disabled, with the reason beside it (as in Settings).
import { Link } from '@tanstack/react-router'
import { type FormEvent, useId, useRef, useState } from 'react'
import { isApiError } from '../api/client'
import { useCreateDeviceRequest } from '../api/queries'
import type { SignalFamily } from '../api/types'
import { useI18n } from '../i18n'
import { FAMILIES, MAX_OPEN_REQUESTS } from '../lib/signals'
import { Btn, Seg } from './d'

/** An email in a reason: the API refuses it, so the form says so first. */
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/

/** The subject's id as the API takes it: an app.installation.id, or a user.hash. */
export const SUBJECT = {
  install: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  user: /^[A-Za-z0-9+/=_.:-]{1,256}$/,
} as const

export const FAMILY_KEY = {
  summary: 'signals.family.summary',
  errors: 'signals.family.errors',
  logs: 'signals.family.logs',
  spans: 'signals.family.spans',
  network: 'signals.family.network',
  metrics: 'signals.family.metrics',
  replay: 'signals.family.replay',
  resources: 'signals.family.resources',
} as const satisfies Record<SignalFamily, string>

const DAYS = ['1', '3', '7'] as const

/**
 * The button and its dialog. With `install`, the request is for that install (a session's
 * device); with `user`, for that app user (the People page); without, the dialog asks for an
 * install id or a user hash.
 */
export function DeviceData({
  orgId,
  projectId,
  install,
  user,
  canEdit,
  label,
  buttonClass,
}: {
  orgId: string
  projectId: string
  install?: string
  user?: string
  /** Undefined while the viewer's role loads: disabled, without the members' note. */
  canEdit: boolean | undefined
  label: string
  buttonClass?: 'solid' | 'ghost'
}) {
  const { t } = useI18n()
  const dialog = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const noteId = useId()
  const create = useCreateDeviceRequest(orgId, projectId)
  const [kind, setKind] = useState<'send' | 'delete'>('send')
  const [subjectKind, setSubjectKind] = useState<'install' | 'user'>('install')
  const [subject, setSubject] = useState('')
  const [all, setAll] = useState(true)
  const [families, setFamilies] = useState<SignalFamily[]>([])
  const [days, setDays] = useState<(typeof DAYS)[number]>('7')
  const [reason, setReason] = useState('')
  const reasonId = useId()
  const fixed = install ?? user
  const value = fixed ?? subject.trim()
  const validSubject = fixed ? true : SUBJECT[subjectKind].test(value)
  // Why, as Activity logs it (APS-454): never a name or an email, which the API refuses too.
  const why = reason.trim()
  const hasEmail = EMAIL.test(why)
  const ready = validSubject && (all || families.length > 0) && why.length > 0 && !hasEmail

  function open() {
    create.reset()
    dialog.current?.showModal()
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!ready) return
    create.mutate({
      kind,
      subject_kind: install ? 'install' : user ? 'user' : subjectKind,
      subject: value,
      ...(all ? {} : { families: FAMILIES.filter((f) => families.includes(f)) }),
      days: Number(days),
      reason: why,
    })
  }

  return (
    <>
      <Btn
        variant={buttonClass}
        disabled={!canEdit}
        aria-describedby={canEdit === false ? noteId : undefined}
        onClick={open}
      >
        {label}
      </Btn>
      {canEdit !== false ? null : (
        <span id={noteId} className="dd-note">
          {t('settings.adminOnly')}
        </span>
      )}
      <dialog ref={dialog} aria-labelledby={titleId} className="dd">
        <form className="sections dd-form" style={{ gap: 14 }} onSubmit={submit}>
          <h2 id={titleId} style={{ fontSize: 17 }}>
            {t('device.title')}
          </h2>
          {create.isSuccess ? (
            <>
              <p role="status" className="se-p">
                {t(kind === 'send' ? 'device.sent' : 'device.deleteSent')}{' '}
                <Link
                  className="ul"
                  to="/o/$orgId/p/$projectId/signals"
                  params={{ orgId, projectId }}
                  // The scope comes along, so the app in the top bar stays the same.
                  search={(prev: Record<string, unknown>) => prev}
                  hash="requests"
                  onClick={() => dialog.current?.close()}
                >
                  {t('signals.requests')}
                </Link>
              </p>
              <div className="acts">
                <Btn onClick={() => dialog.current?.close()}>{t('device.close')}</Btn>
              </div>
            </>
          ) : (
            <>
              {fixed ? (
                <p className="se-p">
                  {t(install ? 'device.forInstall' : 'device.forUser')}{' '}
                  <code translate="no">{`…${fixed.slice(-4)}`}</code>
                </p>
              ) : (
                <div className="dd-field">
                  <span className="dd-k">{t('device.subject')}</span>
                  <Seg
                    label={t('device.subject')}
                    options={[
                      { value: 'install', label: t('device.subject.install') },
                      { value: 'user', label: t('device.subject.user') },
                    ]}
                    value={subjectKind}
                    onChange={setSubjectKind}
                  />
                  <input
                    className="input mono"
                    aria-label={t(
                      subjectKind === 'install' ? 'device.subject.install' : 'device.subject.user',
                    )}
                    placeholder={subjectKind === 'install' ? '0192f3a4-…' : 'user.hash'}
                    translate="no"
                    autoComplete="off"
                    spellCheck={false}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>
              )}
              <div className="dd-field">
                <span className="dd-k">{t('device.kind')}</span>
                <Seg
                  label={t('device.kind')}
                  options={[
                    { value: 'send', label: t('device.kind.send') },
                    { value: 'delete', label: t('device.kind.delete') },
                  ]}
                  value={kind}
                  onChange={setKind}
                />
              </div>
              <fieldset className="dd-field">
                <legend className="dd-k">{t('device.families')}</legend>
                <label className="se-chk">
                  <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
                  {t('device.families.all')}
                </label>
                {all ? null : (
                  <div className="dd-fams">
                    {FAMILIES.map((f) => (
                      <label key={f} className="se-chk">
                        <input
                          type="checkbox"
                          checked={families.includes(f)}
                          onChange={(e) =>
                            setFamilies((xs) =>
                              e.target.checked ? [...xs, f] : xs.filter((x) => x !== f),
                            )
                          }
                        />
                        {t(FAMILY_KEY[f])}
                      </label>
                    ))}
                  </div>
                )}
              </fieldset>
              <div className="dd-field">
                <span className="dd-k">{t('device.days')}</span>
                <Seg
                  label={t('device.days')}
                  options={DAYS.map((d) => ({
                    value: d,
                    label: t('device.daysN', { n: Number(d) }),
                  }))}
                  value={days}
                  onChange={setDays}
                />
              </div>
              <div className="dd-field">
                <label className="dd-k" htmlFor={reasonId}>
                  {t('device.reason')}
                </label>
                <input
                  id={reasonId}
                  className="input"
                  required
                  maxLength={200}
                  placeholder={t('device.reasonPlaceholder')}
                  aria-invalid={hasEmail || undefined}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <span className={hasEmail ? 'se-hint dd-err' : 'se-hint'}>
                  {t(hasEmail ? 'device.reasonEmail' : 'device.reasonNote')}
                </span>
              </div>
              <p className="se-hint">
                {t(kind === 'send' ? 'device.sendWhat' : 'device.deleteWhat', {
                  days: Number(days),
                })}
              </p>
              {create.error ? (
                <p role="alert" className="se-hint dd-err">
                  {isApiError(create.error, 409)
                    ? t('device.tooMany', { max: MAX_OPEN_REQUESTS })
                    : isApiError(create.error, 403)
                      ? t('settings.adminOnly')
                      : isApiError(create.error, 400)
                        ? t('device.badSubject')
                        : t('state.failed')}
                </p>
              ) : null}
              <div className="acts">
                <Btn
                  type="submit"
                  variant={kind === 'delete' ? undefined : 'solid'}
                  className={kind === 'delete' ? 'dd-danger' : undefined}
                  disabled={!ready || create.isPending}
                >
                  {t(kind === 'send' ? 'device.confirm.send' : 'device.confirm.delete')}
                </Btn>
                <Btn onClick={() => dialog.current?.close()}>{t('settings.cancel')}</Btn>
              </div>
            </>
          )}
        </form>
      </dialog>
    </>
  )
}
